import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { io } from 'socket.io-client';
import { 
  Send, 
  Phone, 
  Video, 
  MoreVertical, 
  Search,
  Circle,
  Clock,
  Paperclip,
  Smile
} from 'lucide-react';
import Avatar from '../common/Avatar';
import apiService from '../../services/api';

const SOCKET_URL = 'https://backendhealthbridge.onrender.com';

const ChatSystem = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Initialiser Socket.io
    const token = localStorage.getItem('token');
    socketRef.current = io(SOCKET_URL, {
      auth: { token }
    });

    socketRef.current.on('connect', () => {
      console.log('✅ Connecté au serveur Socket.io');
    });

    socketRef.current.on('new-message', (message) => {
      // Ne pas ajouter le message si c'est l'utilisateur actuel (pour éviter les doublons)
      // car il a déjà ajouté le message temporaire
      setMessages(prev => {
        // Vérifier si le message existe déjà
        const exists = prev.some(m => m.id === message.id);
        if (!exists && message.senderId !== user.id) {
          return [...prev, message];
        }
        // Si c'est notre propre message confirmé, remplacer le temporaire
        if (message.senderId === user.id) {
          return prev.map(m => 
            m.id.toString().startsWith('temp-') && m.content === message.content
              ? message
              : m
          );
        }
        return prev;
      });
      scrollToBottom();
    });

    socketRef.current.on('message-sent', (message) => {
      console.log('Message envoyé:', message);
    });

    socketRef.current.on('error', (error) => {
      console.error('Erreur Socket.io:', error);
    });

    return () => {
      socketRef.current.disconnect();
    };
  }, []);

  useEffect(() => {
    // Charger les utilisateurs en ligne
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadMessages(selectedUser.id);
    }
  }, [selectedUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadUsers = async () => {
    try {
      const response = await apiService.getAllUsers();
      if (response.success) {
        // Filtrer l'utilisateur actuel
        const otherUsers = response.data.filter(u => u.id !== user.id);
        setUsers(otherUsers);
        if (otherUsers.length > 0 && !selectedUser) {
          setSelectedUser(otherUsers[0]);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    }
  };

  const loadMessages = async (userId) => {
    try {
      const response = await apiService.getMessagesWithUser(userId);
      if (response.success) {
        setMessages(response.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSendMessage = () => {
    if (newMessage.trim() && selectedUser && socketRef.current) {
      const messageContent = newMessage.trim();
      
      // Créer un message temporaire pour l'expéditeur
      const tempMessage = {
        id: `temp-${Date.now()}`, // ID temporaire
        senderId: user.id,
        receiverId: selectedUser.id,
        content: messageContent,
        sender: {
          id: user.id,
          name: user.name,
          email: user.email,
          avatar: user.avatar
        },
        createdAt: new Date().toISOString()
      };
      
      // Ajouter immédiatement le message à l'interface
      setMessages(prev => [...prev, tempMessage]);
      
      // Effacer le champ de saisie
      setNewMessage('');
      
      // Envoyer le message via Socket.io
      socketRef.current.emit('send-message', {
        receiverId: selectedUser.id,
        content: messageContent
      });
      
      // Scroll vers le bas après ajout
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const messageDate = new Date(timestamp);
    
    // Formater la date complète et l'heure
    return messageDate.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className=" px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div
          
          
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Chat avec les Spécialistes</h1>
          <p className="text-gray-600 mt-2">Communiquez en temps réel avec les médecins spécialistes</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-200px)]">
          {/* Doctors List */}
          <div
            
            
            
            className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Rechercher un médecin..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            
            <div className="overflow-y-auto h-[calc(100%-80px)]">
              {filteredUsers.map((chatUser) => (
                <div
                  key={chatUser.id}
                  onClick={() => setSelectedUser(chatUser)}
                  className={`p-4 border-b border-gray-100 cursor-pointer transition-colors duration-200 ${
                    selectedUser?.id === chatUser.id ? 'bg-primary-50 border-primary-200' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Avatar
                      name={chatUser.name}
                      size="md"
                      isOnline={chatUser.isOnline}
                      showOnlineStatus={true}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <h3 className={`text-sm font-medium truncate ${chatUser.isOnline ? 'text-gray-900' : 'text-gray-400'}`}>
                            {chatUser.name}
                          </h3>
                          {chatUser.isOnline ? (
                            <span className="text-xs text-green-600 font-medium">En ligne</span>
                          ) : (
                            <span className="text-xs text-gray-400">Hors ligne</span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 capitalize truncate">{chatUser.role}</p>
                      {chatUser.lastActiveAt && (
                        <p className="text-xs text-gray-400">
                          {chatUser.isOnline 
                            ? 'Actif maintenant' 
                            : `Dernière activité: ${formatTime(chatUser.lastActiveAt)}`
                          }
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat Area */}
          <div
            
            
            
            className="lg:col-span-3 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col"
          >
            {/* Chat Header */}
            {selectedUser && (
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Avatar
                    name={selectedUser.name}
                    size="md"
                    isOnline={selectedUser.isOnline}
                    showOnlineStatus={true}
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedUser.name}</h3>
                    <p className="text-sm text-gray-600 capitalize">{selectedUser.role}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <Phone className="h-5 w-5" />
                  </button>
                  <button
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <Video className="h-5 w-5" />
                  </button>
                  <button
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message, index) => {
                const isOwnMessage = message.senderId === user.id;
                return (
                  <div
                    key={message.id || index}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      isOwnMessage 
                        ? 'bg-primary-600 text-white' 
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      <p className="text-sm">{message.content}</p>
                      <div className={`flex items-center mt-1 text-xs ${
                        isOwnMessage ? 'text-primary-100' : 'text-gray-500'
                      }`}>
                        <Clock className="h-3 w-3 mr-1" />
                        {formatTime(message.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button
                  
                  
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                >
                  <Paperclip className="h-5 w-5" />
                </button>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Tapez votre message..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-10"
                  />
                  <button
                    
                    
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <Smile className="h-4 w-4" />
                  </button>
                </div>
                <button
                  
                  
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors duration-200"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatSystem;
